import React, { useState, useMemo, useCallback } from "react";
import { DateTimeDisplay, Breadcrumbs, StatusMessage } from "../../components/ui";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";
import Modal from "../../components/ui/Modal";
import ReportForm from "../../components/forms/ReportForm";
import SortIcon from "../../components/ui/Sorting";
import Pagination from "../../components/ui/Pagination";
import { getAlarmReport, downloadAlarmReport } from "../../services/alarms/alarm.service";
import { AlarmReportRequest, AlarmReportResponse } from "../../services/alarms/alarm.types";
import { buildAlarmReportRequest, type ApiPageBase } from "../../services/alarms/alarmReportRequestBuilder";
import { formatDateTime } from "../../helpers/formatDateTime";

type SortDirection = 'asc' | 'desc';

type GroupedAlarmRow = {
  siteRegistryId: number | null;
  siteName: string;
  alarmPathId: number | null;
  pathToAlarm: string;
  priority: number | null;
  alarmCount: number;
};

const toLowerText = (value?: string | null): string => (value ?? '').toLowerCase();

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object';

const getFirstDefined = (source: Record<string, unknown>, keys: string[]): unknown => {
  for (const key of keys) {
    if (Object.hasOwn(source, key) && source[key] != null) {
      return source[key];
    }
  }

  return undefined;
};

const toNumberOrNull = (value: unknown): number | null => {
  const num = Number(value);
  if (Number.isFinite(num)) {
    return num;
  }

  return null;
};

const toTextOrFallback = (value: unknown, fallback = '-'): string => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }

  return fallback;
};

const toGroupedAlarmRow = (row: unknown): GroupedAlarmRow | null => {
  if (!isRecord(row)) {
    return null;
  }

  const alarmCountValue = getFirstDefined(row, ['alarmCount', 'AlarmCount', 'count', 'Count']);
  const alarmCount = Number(alarmCountValue);
  if (!Number.isFinite(alarmCount)) {
    return null;
  }

  const siteRegistryIdValue = getFirstDefined(row, ['siteRegistryId', 'SiteRegistryId', 'siteIdentifierRegistryId', 'sirId']);
  const siteNameValue = getFirstDefined(row, ['siteName', 'SiteName']);
  const alarmPathIdValue = getFirstDefined(row, ['alarmPathId', 'AlarmPathId']);
  const pathToAlarmValue = getFirstDefined(row, ['pathToAlarm', 'PathToAlarm', 'alarmPath', 'AlarmPath']);
  const priorityValue = getFirstDefined(row, ['priority', 'Priority']);

  return {
    siteRegistryId: toNumberOrNull(siteRegistryIdValue),
    siteName: toTextOrFallback(siteNameValue),
    alarmPathId: toNumberOrNull(alarmPathIdValue),
    pathToAlarm: toTextOrFallback(pathToAlarmValue),
    priority: toNumberOrNull(priorityValue),
    alarmCount,
  };
};

const normalizePathKey = (pathToAlarm: string): string => toLowerText(pathToAlarm.trim());

const mergeGroupedRowSiteInfo = (existing: GroupedAlarmRow, incoming: GroupedAlarmRow): void => {
  if (existing.siteRegistryId == null || incoming.siteRegistryId == null) {
    existing.siteRegistryId = null;
    existing.siteName = 'Multiple Sites';
    return;
  }

  if (existing.siteRegistryId !== incoming.siteRegistryId) {
    existing.siteRegistryId = null;
    existing.siteName = 'Multiple Sites';
  }
};

const mergeGroupedRowPriority = (existing: GroupedAlarmRow, incoming: GroupedAlarmRow): void => {
  if (existing.priority == null || incoming.priority == null) {
    existing.priority = null;
    return;
  }

  if (existing.priority !== incoming.priority) {
    existing.priority = null;
  }
};

const aggregateGroupedRowsByPath = (rows: GroupedAlarmRow[]): GroupedAlarmRow[] => {
  const grouped = new Map<string, GroupedAlarmRow>();

  rows.forEach(row => {
    const pathKey = normalizePathKey(row.pathToAlarm);
    const existing = grouped.get(pathKey);

    if (!existing) {
      grouped.set(pathKey, { ...row });
      return;
    }

    existing.alarmCount += row.alarmCount;
    mergeGroupedRowSiteInfo(existing, row);
    mergeGroupedRowPriority(existing, row);
    if (existing.alarmPathId == null && row.alarmPathId != null) {
      existing.alarmPathId = row.alarmPathId;
    }
  });

  return Array.from(grouped.values());
};

const extractGroupedRows = (data: unknown): GroupedAlarmRow[] => {
  if (!isRecord(data)) {
    return [];
  }

  const candidateKeys = ['groupedAlarms', 'alarmGroups', 'groups', 'rows', 'items', 'data'];
  const candidates: unknown[] = [];

  candidateKeys.forEach(key => {
    if (data[key] != null) {
      candidates.push(data[key]);
    }
  });

  const nestedContainer = getFirstDefined(data, ['result', 'results', 'payload']);
  if (isRecord(nestedContainer)) {
    candidateKeys.forEach(key => {
      if (nestedContainer[key] != null) {
        candidates.push(nestedContainer[key]);
      }
    });
  }

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      const mapped = candidate
        .map(toGroupedAlarmRow)
        .filter((row): row is GroupedAlarmRow => row !== null);

      if (mapped.length > 0) {
        return aggregateGroupedRowsByPath(mapped);
      }
    }
  }

  return [];
};

const getAcknowledgedSortValue = (acknowledged: boolean): number => {
  if (acknowledged) {
    return 1;
  }

  return 0;
};

const getMultiplier = (sortOrder: SortDirection): number => {
  if (sortOrder === 'asc') {
    return 1;
  }

  return -1;
};

const getColumnSortOrder = (activeColumn: string, column: string, order: SortDirection): SortDirection | null => {
  if (activeColumn === column) {
    return order;
  }

  return null;
};

const getAckBadgeClassName = (acknowledged: boolean): string => {
  if (acknowledged) {
    return 'badge--success';
  }

  return 'badge--danger';
};

const getAckLabel = (acknowledged: boolean): string => {
  if (acknowledged) {
    return 'Acknowledged';
  }

  return 'Unacknowledged';
};

const getAlarmStateClassName = (alarmStateName: string): string => {
  const normalized = toLowerText(alarmStateName);

  if (normalized === 'reset') {
    return 'status status__reset';
  }

  if (normalized === 'acknowledged') {
    return 'status status__completed';
  }

  if (normalized === 'active') {
    return 'status status__active';
  }

  if (normalized === 'normal') {
    return 'status status__normal';
  }

  if (normalized === 'disabled') {
    return 'status status__disabled';
  }

  return 'status';
};

const compareAlarmValues = (
  a: AlarmReportResponse['alarms'][number],
  b: AlarmReportResponse['alarms'][number],
  sortColumn: string
): number => {
  switch (sortColumn) {
    case 'site':
      return toLowerText(a.siteName).localeCompare(toLowerText(b.siteName));
    case 'path':
      return toLowerText(a.alarmPath).localeCompare(toLowerText(b.alarmPath));
    case 'message':
      return toLowerText(a.alarmMessage).localeCompare(toLowerText(b.alarmMessage));
    case 'priority':
      return (a.priority ?? -1) - (b.priority ?? -1);
    case 'status':
      return getAcknowledgedSortValue(a.acknowledged) - getAcknowledgedSortValue(b.acknowledged);
    case 'state':
      return toLowerText(a.alarmStateName).localeCompare(toLowerText(b.alarmStateName));
    case 'timestamp':
      return new Date(a.alarmTimestamp).getTime() - new Date(b.alarmTimestamp).getTime();  
    default:
      return 0;
  }
};

const createAlarmComparator = (sortColumn: string, sortOrder: SortDirection) => {
  const multiplier = getMultiplier(sortOrder);

  return (a: AlarmReportResponse['alarms'][number], b: AlarmReportResponse['alarms'][number]) =>
    compareAlarmValues(a, b, sortColumn) * multiplier;
};

const sortAlarms = (
  alarms: AlarmReportResponse['alarms'] | undefined,
  sortColumn: string,
  sortOrder: SortDirection
) => {
  if (!alarms || alarms.length === 0) {
    return [];
  }

  const copy = [...alarms];
  if (!sortColumn) {
    return copy;
  }

  copy.sort(createAlarmComparator(sortColumn, sortOrder));

  return copy;
};

const compareGroupedValues = (a: GroupedAlarmRow, b: GroupedAlarmRow, sortColumn: string): number => {
  switch (sortColumn) {
    case 'site':
      return toLowerText(a.siteName).localeCompare(toLowerText(b.siteName));
    case 'path':
      return toLowerText(a.pathToAlarm).localeCompare(toLowerText(b.pathToAlarm));
    case 'priority':
      return (a.priority ?? -1) - (b.priority ?? -1);
    case 'count':
      return a.alarmCount - b.alarmCount;
    default:
      return 0;
  }
};

const createGroupedComparator = (sortColumn: string, sortOrder: SortDirection) => {
  const multiplier = getMultiplier(sortOrder);

  return (a: GroupedAlarmRow, b: GroupedAlarmRow) => compareGroupedValues(a, b, sortColumn) * multiplier;
};

const sortGroupedRows = (
  rows: GroupedAlarmRow[] | undefined,
  sortColumn: string,
  sortOrder: SortDirection
) => {
  if (!rows || rows.length === 0) {
    return [];
  }

  const copy = [...rows];
  if (!sortColumn) {
    return copy;
  }

  copy.sort(createGroupedComparator(sortColumn, sortOrder));
  return copy;
};

type ReportStatsProps = {
  reportData: AlarmReportResponse | null;
  groupedRows: GroupedAlarmRow[];
  isGroupedMode: boolean;
};

const ReportStats: React.FC<ReportStatsProps> = ({ reportData, groupedRows, isGroupedMode }) => {
  if (!reportData) {
    return null;
  }

  const totalFromGroupedRows = groupedRows.reduce((sum, row) => sum + row.alarmCount, 0);
  let totalAlarms = reportData.statistics?.totalAlarms ?? 0;
  const acknowledgedCount = reportData.statistics?.acknowledgedCount ?? 0;
  const unacknowledgedCount = reportData.statistics?.unacknowledgedCount ?? 0;
  let acknowledgedDisplay: string | number;
  let unacknowledgedDisplay: string | number;

  if (isGroupedMode) {
    if (reportData.totalCount > 0) {
      totalAlarms = reportData.totalCount;
    } else {
      totalAlarms = totalFromGroupedRows;
    }

    // Grouped rows do not reliably include acknowledged breakdowns.
    acknowledgedDisplay = '-';
    unacknowledgedDisplay = '-';
  } else {
    acknowledgedDisplay = acknowledgedCount.toLocaleString();
    unacknowledgedDisplay = unacknowledgedCount.toLocaleString();
  }

  return (
    <div className="stats-grid mt-2">
      <div className="card card__active-alarms">
        <div className="card__icon--alarm" />
        <div className="card__content">
          <h6>Report Summary</h6>
          <h2>{totalAlarms.toLocaleString()}</h2>
          <span>Total Alarms</span>
        </div>
      </div>
      <div className="card card__acknowledged-alarms">
        <div className="card__icon--alarm" />
        <div className="card__content">
          <h6>Report Summary</h6>
          <h2>{acknowledgedDisplay}</h2>
          <span>Acknowledged</span>
        </div>
      </div>
      <div className="card card__unacknowledged-alarms">
        <div className="card__icon--alarm" />
        <div className="card__content">
          <h6>Report Summary</h6>
          <h2>{unacknowledgedDisplay}</h2>
          <span>Unacknowledged</span>
        </div>
      </div>
    </div>
  );
};

type ReportResultsProps = {
  reportData: AlarmReportResponse | null;
  sortedData: AlarmReportResponse['alarms'];
  groupedRows: GroupedAlarmRow[];
  isGroupedMode: boolean;
  sortColumn: string;
  sortOrder: SortDirection;
  onSort: (column: string) => void;
};

const getAriaSortValue = (activeColumn: string, column: string, order: SortDirection): 'ascending' | 'descending' | 'none' => {
  if (activeColumn !== column) {
    return 'none';
  }

  if (order === 'asc') {
    return 'ascending';
  }

  return 'descending';
};

const REPORT_COLUMNS = [
  { key: 'site', label: 'Site' },
  { key: 'path', label: 'Path' },
  { key: 'message', label: 'Message' },
  { key: 'priority', label: 'Priority' },
  { key: 'status', label: 'Status' },
  { key: 'state', label: 'State' },
  { key: 'timestamp', label: 'Timestamp' },
] as const;

const GROUPED_REPORT_COLUMNS = [
  { key: 'site', label: 'Site' },
  { key: 'path', label: 'Path' },
  { key: 'priority', label: 'Priority' },
  { key: 'count', label: 'Alarm Count' },
] as const;

type ReportTableHeaderProps = {
  sortColumn: string;
  sortOrder: SortDirection;
  onSort: (column: string) => void;
};

const ReportTableHeader: React.FC<ReportTableHeaderProps> = ({ sortColumn, sortOrder, onSort }) => (
  <thead>
    <tr>
      {REPORT_COLUMNS.map(column => (
        <th
          key={column.key}
          onClick={onSort.bind(null, column.key)}
          aria-sort={getAriaSortValue(sortColumn, column.key, sortOrder)}
          className="th--sortable"
        >
          {column.label} <SortIcon sortOrder={getColumnSortOrder(sortColumn, column.key, sortOrder)} />
        </th>
      ))}
    </tr>
  </thead>
);

type ReportTableBodyProps = {
  sortedData: AlarmReportResponse['alarms'];
};

const ReportTableBody: React.FC<ReportTableBodyProps> = ({ sortedData }) => {
  if (sortedData.length === 0) {
    return (
      <tbody>
        <tr>
          <td colSpan={7} className="table-responsive-nodata-text">
            No Results
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody>
      {sortedData.map(alarm => (
        <tr key={alarm.id}>
          <td>{alarm.siteName}</td>
          <td className="path-name">{alarm.alarmPath}</td>
          <td className="path-name">{alarm.alarmMessage}</td>
          <td><span className="badge badge--priority">{alarm.priority}</span></td>
          <td>
            <span className={`badge ${getAckBadgeClassName(alarm.acknowledged)}`}>
              {getAckLabel(alarm.acknowledged)}
            </span>
          </td>
          <td>
            <span className={getAlarmStateClassName(alarm.alarmStateName)}>{alarm.alarmStateName}</span>
          </td>
          <td>{formatDateTime(alarm.alarmTimestamp)}</td>
        </tr>
      ))}
    </tbody>
  );
};

const ReportResults: React.FC<ReportResultsProps> = ({
  reportData,
  sortedData,
  groupedRows,
  isGroupedMode,
  sortColumn,
  sortOrder,
  onSort,
}) => {
  if (!reportData) {
    return null;
  }

  const showGroupedRows = isGroupedMode && groupedRows.length > 0;
  let displayedCount = reportData.alarms.length;
  if (showGroupedRows) {
    displayedCount = groupedRows.length;
  }

  let footerUnit = 'alarm(s)';
  if (showGroupedRows) {
    footerUnit = 'grouped path(s)';
  }

  return (
    <section className='page mt-2'>
      <div className="table-container">
        <h3 className="heading heading__title--reports mt-0">Report Results</h3>
        <div className="table-responsive">
          {showGroupedRows && (
            <table className="table">
              <thead>
                <tr>
                  {GROUPED_REPORT_COLUMNS.map(column => (
                    <th
                      key={column.key}
                      onClick={onSort.bind(null, column.key)}
                      aria-sort={getAriaSortValue(sortColumn, column.key, sortOrder)}
                      className="th--sortable"
                    >
                      {column.label} <SortIcon sortOrder={getColumnSortOrder(sortColumn, column.key, sortOrder)} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupedRows.map((row, index) => (
                  <tr key={`${row.siteRegistryId ?? 'na'}-${row.alarmPathId ?? 'na'}-${row.priority ?? 'na'}-${index}`}>
                    <td>{row.siteName}</td>
                    <td className="path-name">{row.pathToAlarm}</td>
                    <td><span className="badge badge--priority">{row.priority ?? '-'}</span></td>
                    <td><span className="badge badge--generated">{row.alarmCount}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!showGroupedRows && (
            <table className="table">
              <ReportTableHeader sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} />
              <ReportTableBody sortedData={sortedData} />
            </table>
          )}
        </div>

        <div className="badge badge--generated mt-2">
          Showing {displayedCount} {footerUnit} on this page | Total: {reportData.totalCount.toLocaleString()} alarm(s)
        </div>
      </div>
    </section>
  );
};

const ReportsDetails: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reportData, setReportData] = useState<AlarmReportResponse | null>(null);
  const [showErrorSummary, setShowErrorSummary] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [apiPageBase, setApiPageBase] = useState<ApiPageBase>('unknown');
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [groupedRows, setGroupedRows] = useState<GroupedAlarmRow[]>([]);

  // Form state
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('00:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('23:59');
  const [messageFilter, setMessageFilter] = useState('');
  const [monitoredOnly, setMonitoredOnly] = useState(true);
  const [groupAlarms, setGroupAlarms] = useState(false);
  const [priority, setPriority] = useState<number | null>(null);
  const [minPriority, setMinPriority] = useState(1);
  const [maxPriority, setMaxPriority] = useState(10000);
  const [siteIds, setSiteIds] = useState<number[]>([]);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [alarmPathIds, setAlarmPathIds] = useState<number[]>([]);
  const [alarmStateIds, setAlarmStateIds] = useState<number[]>([]);
  const [acknowledgedBy, setAcknowledgedBy] = useState('');
  const [includeStatistics, setIncludeStatistics] = useState(true);
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else {
        setSortOrder('asc');
      }
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const sortedData = useMemo(() => sortAlarms(reportData?.alarms, sortColumn, sortOrder), [reportData?.alarms, sortColumn, sortOrder]);
  const sortedGroupedRows = useMemo(() => sortGroupedRows(groupedRows, sortColumn, sortOrder), [groupedRows, sortColumn, sortOrder]);

  const handleOpenModal = () => {
    setShowErrorSummary(false);
    setSubmitError(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setShowErrorSummary(false);
    setSubmitError(null);
    setStartDate('');
    setStartTime('00:00');
    setEndDate('');
    setEndTime('23:59');
    setMessageFilter('');
    setMonitoredOnly(true);
    setGroupAlarms(false);
    setPriority(null);
    setMinPriority(1);
    setMaxPriority(10000);
    setSiteIds([]);
    setCategoryIds([]);
    setAlarmPathIds([]);
    setAlarmStateIds([]);
    setAcknowledgedBy('');
    setIncludeStatistics(true);
    setPageSize(100);
  };

  const handleCloseSubmitError = () => {
    setSubmitError(null);
  };

  const handleCloseErrorSummary = () => {
    setShowErrorSummary(false);
  };

  const handleClearReport = () => {
    setReportData(null);
    setGroupedRows([]);
    setCurrentPage(1);
    setApiPageBase('unknown');
    setStartDate('');
    setStartTime('00:00');
    setEndDate('');
    setEndTime('23:59');
    setMessageFilter('');
    setMonitoredOnly(true);
    setGroupAlarms(false);
    setPriority(null);
    setMinPriority(1);
    setMaxPriority(10000);
    setSiteIds([]);
    setCategoryIds([]);
    setAlarmPathIds([]);
    setAlarmStateIds([]);
    setAcknowledgedBy('');
    setIncludeStatistics(true);
    setPageSize(100);
    setSortColumn('');
    setSortOrder('asc');
    setShowErrorSummary(false);
    setIsFormValid(false);
  };

  const handleValidation = useCallback((isValid: boolean, _errors: unknown[]) => {
    setIsFormValid(isValid);
  }, []);

  const handleDownload = async () => {
    if (!reportData) { return; }

    setIsDownloading(true);
    try {
      const {
        page: _page,
        pageSize: _pageSize,
        sortBy: _sortBy,
        sortDirection: _sortDirection,
        includeStatistics: _includeStatistics,
        ...request
      }: AlarmReportRequest = buildReportRequest(1);

      console.info('[ReportsDetails] Download request:', JSON.stringify(request, null, 2));

      const { blob, filename } = await downloadAlarmReport(request);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.info('[ReportsDetails] Failed to download report:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const buildReportRequest = (uiPage: number, forcedApiPageBase?: 'zero' | 'one'): AlarmReportRequest =>
    buildAlarmReportRequest({
      startDate,
      startTime,
      endDate,
      endTime,
      messageFilter,
      monitoredOnly,
      groupAlarms,
      priority,
      minPriority,
      maxPriority,
      siteIds,
      categoryIds,
      alarmPathIds,
      alarmStateIds,
      acknowledgedBy,
      includeStatistics,
      pageSize,
      uiPage,
      apiPageBase,
      forcedApiPageBase,
    });

  async function handlePageChange(page: number) {
    if (!reportData) {
      return;
    }
    
    setCurrentPage(page);
    setIsLoading(true);

    let forcedPageBase: 'zero' | 'one';
    if (apiPageBase === 'unknown') {
      forcedPageBase = 'zero';
    } else {
      forcedPageBase = apiPageBase;
    }

    const request = buildReportRequest(page, forcedPageBase);

    try {
      const response = await getAlarmReport(request);
      if (response.data) {
        setReportData(response.data);
        if (groupAlarms) {
          setGroupedRows(extractGroupedRows(response.data as unknown));
        } else {
          setGroupedRows([]);
        }
      }
    } catch (err: unknown) {
      let errorMessage: unknown = err;
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      console.info('[ReportsDetails] Failed to load page:', errorMessage);
    } finally {
      setIsLoading(false);
    }
  }

  const shouldTryAlternatePaging = (
    response: Awaited<ReturnType<typeof getAlarmReport>>,
    requestPage: number
  ): boolean => {
    const totalFromStats = response.data?.statistics?.totalAlarms ?? 0;
    const total = response.data?.totalCount ?? totalFromStats;

    return (
      apiPageBase === 'unknown' &&
      !!response.data &&
      total > 0 &&
      (response.data.alarms?.length ?? 0) === 0 &&
      requestPage === 1
    );
  };

  const closeModalWithReportData = (data: AlarmReportResponse) => {
    setReportData(data);
    if (groupAlarms) {
      setGroupedRows(extractGroupedRows(data as unknown));
    } else {
      setGroupedRows([]);
    }
    setIsModalOpen(false);
    setShowErrorSummary(false);
  };

  const resolveReportData = async (
    response: Awaited<ReturnType<typeof getAlarmReport>>,
    requestPage: number,
    initialPageBase: 'zero' | 'one'
  ): Promise<AlarmReportResponse | null> => {
    if (shouldTryAlternatePaging(response, requestPage)) {
      let alternatePageBase: 'zero' | 'one' = 'zero';
      if (initialPageBase === 'zero') {
        alternatePageBase = 'one';
      }
      console.info('[ReportsDetails] Empty first page with non-zero totals; retrying with alternate page base', {
        initialPageBase,
        alternatePageBase,
      });

      const retryReq = buildReportRequest(1, alternatePageBase);
      const retryResp = await getAlarmReport(retryReq);
      console.info('[ReportsDetails] Report response (retry alternate page base):', {
        success: retryResp.success,
        totalCount: retryResp.data?.totalCount,
        alarmsCount: retryResp.data?.alarms?.length,
        page: retryResp.data?.page,
        pageSize: retryResp.data?.pageSize,
        totalPages: retryResp.data?.totalPages,
        statisticsTotalAlarms: retryResp.data?.statistics?.totalAlarms,
        firstAlarm: retryResp.data?.alarms?.[0],
      });

      if (retryResp.success && retryResp.data) {
        setApiPageBase(alternatePageBase);
        return retryResp.data;
      }
    }

    if (response.success && response.data) {
      setApiPageBase(initialPageBase);
      return response.data;
    }

    return null;
  };

  async function handleSubmit() {
    if (!isFormValid) {
      setShowErrorSummary(true);
      return;
    }

    setShowErrorSummary(false);
    setSubmitError(null);
    setCurrentPage(1);
    setIsLoading(true);

    let initialPageBase: 'zero' | 'one' = 'zero';
    if (groupAlarms) {
      initialPageBase = 'one';
    }
    const request = buildReportRequest(1, initialPageBase);

    console.info('[ReportsDetails] Submitting report request:',   {
      apiPageBase,
      priority,
      minPriority,
      maxPriority,
      siteIds,
      siteRegistryIds: request.siteRegistryIds,
      alarmPathIds: request.alarmPathIds,
      requestPage: request.page,
      fullRequest: JSON.stringify(request, null, 2)
    });

    try {
      const response = await getAlarmReport(request);
      console.info('[ReportsDetails] Report response:', {
        success: response.success,
        totalCount: response.data?.totalCount,
        alarmsCount: response.data?.alarms?.length,
        hasAlarms: !!response.data?.alarms,
        page: response.data?.page,
        pageSize: response.data?.pageSize,
        totalPages: response.data?.totalPages,
        statisticsTotalAlarms: response.data?.statistics?.totalAlarms,
        firstAlarm: response.data?.alarms?.[0],
        fullResponse: response.data
      });

      if (!response.success) {
        const msg = response.error?.message ?? 'Failed to generate report';
        setSubmitError(msg);
        return;
      }

      const resolvedData = await resolveReportData(response, request.page ?? 1, initialPageBase);
      if (resolvedData) {
        closeModalWithReportData(resolvedData);
      }
    } catch (err: unknown) {
      let errorMessage: unknown = err;
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      console.info('[ReportsDetails] Failed to generate report:', errorMessage);
      let errMsg = 'Failed to generate report';
      if (err instanceof Error) { errMsg = err.message; }
      setSubmitError(errMsg);
      setShowErrorSummary(true);
    } finally {
      setIsLoading(false);
    }
  }

  const reportForm = (
    <ReportForm
      startDate={startDate}
      startTime={startTime}
      endDate={endDate}
      endTime={endTime}
      messageFilter={messageFilter}
      monitoredOnly={monitoredOnly}
      groupAlarms={groupAlarms}
      priority={priority}
      minPriority={minPriority}
      maxPriority={maxPriority}
      siteIds={siteIds}
      categoryIds={categoryIds}
      alarmPathIds={alarmPathIds}
      alarmStateIds={alarmStateIds}
      acknowledgedBy={acknowledgedBy}
      includeStatistics={includeStatistics}
      pageSize={pageSize}
      onStartDateChange={setStartDate}
      onStartTimeChange={setStartTime}
      onEndDateChange={setEndDate}
      onEndTimeChange={setEndTime}
      onMessageFilterChange={setMessageFilter}
      onMonitoredOnlyChange={setMonitoredOnly}
      onGroupAlarmsChange={setGroupAlarms}
      onPriorityChange={setPriority}
      onMinPriorityChange={setMinPriority}
      onMaxPriorityChange={setMaxPriority}
      onSiteIdsChange={setSiteIds}
      onCategoryIdsChange={setCategoryIds}
      onAlarmPathIdsChange={setAlarmPathIds}
      onAlarmStateIdsChange={setAlarmStateIds}
      onAcknowledgedByChange={setAcknowledgedBy}
      onIncludeStatisticsChange={setIncludeStatistics}
      onPageSizeChange={setPageSize}
      showErrorSummary={showErrorSummary}
      onCloseErrorSummary={handleCloseErrorSummary}
      onValidate={handleValidation}
    />
  );

  const modalLoadingContent = <LoadingSpinner message="Generating report…" className="mt-2" />;

  let modalContent: React.ReactNode;
  if (isLoading) {
    modalContent = modalLoadingContent;
  } else {
    modalContent = (
      <>
        {submitError && (
          <StatusMessage kind="error" onClose={handleCloseSubmitError} className="mt-2">
            {submitError}
          </StatusMessage>
        )}
        {reportForm}
      </>
    );
  }

  const breadcrumbs = useBreadcrumbs({
    currentPageTitle: 'Reports',
  });

  const isPageLoading = isLoading && !isModalOpen;
  let downloadLabel = 'Download Report';
  if (isDownloading) { downloadLabel = 'Downloading\u2026'; }

  return (
    <main> 
        <div className="container mb-2">       
            <section className='page'>
                <div className="heading__section">
                    <div className="heading__title-group">
                        <h2 className="heading__title heading__title--reports">Reports Details Page</h2>
                        <DateTimeDisplay />
                        <Breadcrumbs items={breadcrumbs} />
                    </div>
                    <div>
                        <button type="button" className="btn btn-add-new" onClick={handleOpenModal}>
                            Get Alarm Reports
                        </button>
                    </div>
                </div>

                <div className="alarm-content">
                  <div className="alarm-content--heading" >
                        <h3 className="heading heading__title--reports">Report Details</h3>
                        {reportData && (
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <button
                                  type="button"
                                  className="btn btn-download"
                                  onClick={handleDownload}
                                  disabled={isDownloading}
                                >
                                  {downloadLabel}
                                </button>
                                <button type="button" className="btn-cancel" onClick={handleClearReport}>
                                    Clear Report
                                </button>
                            </div>
                        )}
                    </div>

                    {!reportData && (
                      <p>Click "Get Alarm Reports" to generate a report.</p>
                    )}

                    {reportData && (
                      <div className="badge badge--generated">Generated at: {formatDateTime(reportData.generatedAt)}</div>
                    )}
                </div>
            </section>

            <ReportStats reportData={reportData} groupedRows={sortedGroupedRows} isGroupedMode={groupAlarms} />

            {isPageLoading && (
              <section className='page mt-2'>
                <LoadingSpinner message="Loading report data…" />
              </section>
            )}

            {!isPageLoading && (
              <>
                <ReportResults
                  reportData={reportData}
                  sortedData={sortedData}
                  groupedRows={sortedGroupedRows}
                  isGroupedMode={groupAlarms}
                  sortColumn={sortColumn}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />

                {reportData && reportData.totalPages > 1 && (
                  <div className="mt-2">
                    <Pagination 
                      currentPage={currentPage}
                      totalPages={reportData.totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            )}
        </div>

        <Modal
          isOpen={isModalOpen}
          onCancel={handleCloseModal}
          onConfirm={handleSubmit}
          title="Generate Alarm Report"
          content={modalContent}
        />
    </main>
  );
}
export default ReportsDetails;
